import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import typography from "../../../../cssUtils/typography";
import { ColorsConstants } from "../../../../cssUtils/constants";
import { useAuth } from "../../../../hooks/AuthProvider";
import { TEMPLATE_NAMES_ENDPOINT, USERS_ENDPOINT } from "../../../../config/urls";

const FIELD_OPTIONS = [
  { value: "lead_number", label: "Lead Number (Phone)" },
  { value: "lead_name", label: "Lead Name" },
];

const SAMPLE_VALUES = {
  lead_number: "919876543210",
  lead_name: "Rahul Sharma",
};

// Sequential {{1}}, {{2}}, ... count, matching how the backend
// (templates.utils.get_template_variable_count) counts body placeholders.
const getPlaceholderCount = (template) => {
  const content = template?.template_json?.content || "";
  let count = 0;
  while (content.includes(`{{${count + 1}}}`)) count += 1;
  return count;
};

const buildPreviewText = (template, mapping) => {
  let content = template?.template_json?.content || "";
  mapping.forEach((field, index) => {
    const sample = SAMPLE_VALUES[field] || `{{${index + 1}}}`;
    content = content.split(`{{${index + 1}}}`).join(sample);
  });
  return content;
};

const WhatsAppNotificationPreset = ({ config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState("");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  useEffect(() => {
    const authHeaders = async () => {
      const token = await getValidToken();
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
    };

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      setTemplatesError("");
      try {
        const headers = await authHeaders();
        const response = await fetch(
          `${TEMPLATE_NAMES_ENDPOINT}?variable_count=2`,
          { headers },
        );
        if (!response.ok) throw new Error("Failed to load templates");
        const data = await response.json();
        setTemplates(data.results || []);
      } catch (err) {
        setTemplatesError("Could not load templates. Please try again.");
      } finally {
        setTemplatesLoading(false);
      }
    };

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError("");
      try {
        const headers = await authHeaders();
        const response = await fetch(USERS_ENDPOINT, { headers });
        if (!response.ok) throw new Error("Failed to load users");
        const data = await response.json();
        setUsers(data.results || []);
      } catch (err) {
        setUsersError("Could not load users. Please try again.");
      } finally {
        setUsersLoading(false);
      }
    };

    loadTemplates();
    loadUsers();
  }, []);

  const selectedTemplate =
    templates.find((t) => t.id === config["template_id"]) || null;

  const placeholderCount = getPlaceholderCount(selectedTemplate);
  const variableMapping = config["variable_mapping"] || [];

  const handleTemplateChange = (newValue) => {
    handleConfigChange("template_id", newValue?.id ?? null);

    // Re-sync mapping length to the newly selected template's placeholder
    // count — templates are already filtered to exactly 2, so default fresh
    // slots to the original [lead_number, lead_name] order.
    const nextCount = getPlaceholderCount(newValue);
    const defaultOrder = ["lead_number", "lead_name"];
    const next = Array.from(
      { length: nextCount },
      (_, i) => variableMapping[i] || defaultOrder[i] || "",
    );
    handleConfigChange("variable_mapping", next);
  };

  const handleMappingChange = (index, value) => {
    const next = [...variableMapping];
    next[index] = value;
    handleConfigChange("variable_mapping", next);
  };

  const getUserPhone = (user) => `${user.country_code || ""}${user.dial_code || ""}`;

  const recipientPhoneNumbers = config["recipient_phone_numbers"] || [];
  const selectedUsers = users.filter((u) =>
    recipientPhoneNumbers.includes(getUserPhone(u)),
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          Approved Template
        </Typography>
        <Autocomplete
          options={templates}
          value={selectedTemplate}
          loading={templatesLoading}
          onChange={(_, newValue) => handleTemplateChange(newValue)}
          getOptionLabel={(option) => option?.template_name || ""}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          noOptionsText={
            templatesLoading
              ? "Loading templates…"
              : "No approved templates with exactly 2 variables found"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Template"
              variant="outlined"
              error={!!templatesError}
              helperText={templatesError || undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {templatesLoading && (
                      <CircularProgress color="inherit" size={16} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>

      {selectedTemplate && (
        <Stack spacing={1.5}>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
            Variable Mapping
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor }}>
            Choose which lead field fills each variable in the template.
          </Typography>
          {Array.from({ length: placeholderCount }, (_, index) => (
            <Autocomplete
              key={index}
              options={FIELD_OPTIONS}
              value={FIELD_OPTIONS.find((o) => o.value === variableMapping[index]) || null}
              onChange={(_, newValue) => handleMappingChange(index, newValue?.value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`{{${index + 1}}}`}
                  variant="outlined"
                  size="small"
                />
              )}
              noOptionsText="No fields found"
            />
          ))}

          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mt: 1 }}>
            Preview
          </Typography>
          <Box
            sx={{
              backgroundColor: "#D9FDD3",
              borderRadius: 2,
              p: 2,
              maxWidth: 320,
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }}
          >
            <Typography sx={{ ...typography.bodyLarge, whiteSpace: "pre-wrap" }}>
              {buildPreviewText(selectedTemplate, variableMapping)}
            </Typography>
          </Box>
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          WhatsApp Recipients
        </Typography>
        <Autocomplete
          multiple
          options={users}
          value={selectedUsers}
          loading={usersLoading}
          onChange={(_, newValue) =>
            handleConfigChange(
              "recipient_phone_numbers",
              newValue.map((u) => getUserPhone(u)),
            )
          }
          getOptionLabel={(option) => `${option?.name || ""} (${getUserPhone(option)})`}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          noOptionsText={usersLoading ? "Loading users…" : "No users found"}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Recipients"
              variant="outlined"
              error={!!usersError}
              helperText={usersError || undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading && (
                      <CircularProgress color="inherit" size={16} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

export default WhatsAppNotificationPreset;
